import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Checkbox, FormControlLabel } from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import { PDFViewerProps, PDFViewerViewMode } from './PDFViewer.props';
import {
  PdfPageViewerLoader,
  PdfViewerContentWrapper,
  PdfViewerPageContainer,
  PdfViewerPageHeader,
  PdfViewerStateContainer,
  PdfViewerWrapper,
  THUMBNAIL_WIDTH,
} from './PDFViewer.styles';
import { Logger } from '../../../utilities';

// Set up the PDF.js worker - use local file
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.js';

interface LazyPDFPageProps {
  pageNumber: number;
  width: number;
  totalPages: number;
  allowPageSelection?: boolean;
  isSelected?: boolean;
  onToggle?: (pageNumber: number) => void;
  viewMode?: PDFViewerViewMode;
}

const LazyPDFPage = ({
  pageNumber,
  width,
  totalPages,
  allowPageSelection = false,
  isSelected = false,
  onToggle,
  viewMode = PDFViewerViewMode.DOCUMENT,
}: LazyPDFPageProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  const pageRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setTimeout(() => setShouldAnimate(true), pageNumber * 50);
          // Once visible, we keep it loaded
          observer.disconnect();
        }
      },
      {
        rootMargin: '100px', // Load pages 100px before they become visible
        threshold: 0.1,
      }
    );

    if (pageRef.current) {
      observer.observe(pageRef.current);
    }

    return () => observer.disconnect();
  }, [pageNumber]);

  const handleToggle = () => {
    if (onToggle) {
      onToggle(pageNumber);
    }
  };

  return (
    <PdfViewerPageContainer
      ref={pageRef}
      className={shouldAnimate ? 'animate-in' : ''}
      $viewMode={viewMode}
      $isVisible={isVisible}
      $isSelected={isSelected && allowPageSelection}
      $width={width}>
      <PdfViewerPageHeader>
        <span>
          Page {pageNumber} of {totalPages}
        </span>
        {allowPageSelection && (
          <FormControlLabel
            control={<Checkbox checked={isSelected} onChange={handleToggle} size="small" color="primary" />}
            label="Select for processing"
            labelPlacement="start"
          />
        )}
      </PdfViewerPageHeader>
      {isVisible && (
        <Page pageNumber={pageNumber} renderTextLayer={false} renderAnnotationLayer={false} width={width} scale={1} />
      )}
    </PdfViewerPageContainer>
  );
};

const PDFViewer = ({
  src,
  base64Content,
  allowPageSelection = false,
  selectedPages = new Set(),
  onPageToggle,
  viewMode = PDFViewerViewMode.DOCUMENT,
}: PDFViewerProps) => {
  const [numPages, setNumPages] = useState<number>(0);
  const [pdfData, setPdfData] = useState<string | ArrayBuffer | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const calculatedWidth = useMemo(() => {
    if (viewMode === PDFViewerViewMode.THUMBNAIL) {
      return THUMBNAIL_WIDTH;
    }
    const value = typeof window !== 'undefined' ? Math.min(1200, window.innerWidth - 100) : 1200;
    return value;
  }, [viewMode]);

  useEffect(() => {
    const processPdfData = async () => {
      setLoading(true);
      setError(null);

      try {
        // might be able to use the binary coming from the backend
        if (base64Content) {
          const binaryString = atob(base64Content);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          setPdfData(bytes.buffer);
        } else if (src) {
          setPdfData(src);
        } else {
          setError('No PDF source provided');
        }
      } catch (err) {
        Logger.error('Error processing PDF data:', err);
        setError('Failed to process PDF data');
      } finally {
        setLoading(false);
      }
    };

    processPdfData();
  }, [base64Content, src]);

  const onDocumentLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
  }, []);

  const onDocumentLoadError = useCallback((error: Error) => {
    Logger.error('Error loading PDF:', error);
    setError('Failed to load PDF document');
  }, []);

  if (loading) {
    return (
      <PdfViewerWrapper>
        <PdfPageViewerLoader>Loading PDF...</PdfPageViewerLoader>
      </PdfViewerWrapper>
    );
  }

  if (error) {
    return <PdfViewerStateContainer style={{ color: 'red' }}>Error: {error}</PdfViewerStateContainer>;
  }

  if (!pdfData) {
    return <PdfViewerStateContainer>No PDF available</PdfViewerStateContainer>;
  }

  const pageNumbers = Array.from({ length: numPages }, (_, i) => i + 1);

  return (
    <PdfViewerWrapper $viewMode={viewMode}>
      <Document file={pdfData} onLoadSuccess={onDocumentLoadSuccess} onLoadError={onDocumentLoadError} loading={<></>}>
        <PdfViewerContentWrapper $viewMode={viewMode}>
          {pageNumbers.map(pageNum => (
            <LazyPDFPage
              key={pageNum}
              pageNumber={pageNum}
              width={calculatedWidth}
              totalPages={numPages}
              allowPageSelection={allowPageSelection}
              isSelected={selectedPages.has(pageNum)}
              onToggle={onPageToggle}
              viewMode={viewMode}
            />
          ))}
        </PdfViewerContentWrapper>
      </Document>
    </PdfViewerWrapper>
  );
};

export default PDFViewer;
